import { apiClient } from './client'
import type { CursorPage } from './accounts'
import type { AiCartridgeDraftProposalView, AiRunView, GenerateCartridgeDraftRequest } from '../types/aiCartridgeDraft'

/**
 * Typed fetch wrappers for `module.ai`'s IB-286 cartridge-drafting engine (FR-M7.5, IB-211 Story 4) —
 * `/api/v1/cartridge-drafts` + the `/api/v1/ai-runs/{id}` read this UI uses for honest-stub/citation
 * disclosure. Same thin-wrapper convention as `api/ai.ts`.
 *
 * GUARDED-WRITE CONTRACT: this file never writes canon directly. {@link acceptCartridgeDraft} POSTs
 * `{comment}` to `/cartridge-drafts/{id}/accept` — the one server-side door into a `status='draft'`
 * canon row (`CartridgeDraftProposalService.accept`, ADR-0054). No function here ever constructs a
 * `RuleSet`/`Parameter`/`EligibilityModel`/`Decision` request body itself.
 */

export async function generateCartridgeDraft(
  request: GenerateCartridgeDraftRequest,
): Promise<AiCartridgeDraftProposalView> {
  const { data } = await apiClient.post<AiCartridgeDraftProposalView>('/cartridge-drafts', request)
  return data
}

export async function getCartridgeDraft(id: string, signal?: AbortSignal): Promise<AiCartridgeDraftProposalView> {
  const { data } = await apiClient.get<AiCartridgeDraftProposalView>(`/cartridge-drafts/${id}`, { signal })
  return data
}

export async function listCartridgeDrafts(
  programId: string,
  signal?: AbortSignal,
): Promise<CursorPage<AiCartridgeDraftProposalView>> {
  const { data } = await apiClient.get<CursorPage<AiCartridgeDraftProposalView>>(
    `/cartridge-drafts?program_id=${encodeURIComponent(programId)}`,
    { signal },
  )
  return data
}

/** The human "dispose" half of BR-1102 — accepts a pending proposal. The actual canon draft-create
 * happens entirely server-side (`CartridgeDraftSupportApi`/`EligibilityDraftSupportApi`); this call
 * never carries the drafted content itself, only `comment`. */
export async function acceptCartridgeDraft(id: string, comment: string): Promise<AiCartridgeDraftProposalView> {
  const { data } = await apiClient.post<AiCartridgeDraftProposalView>(`/cartridge-drafts/${id}/accept`, { comment })
  return data
}

export async function rejectCartridgeDraft(id: string, comment: string): Promise<AiCartridgeDraftProposalView> {
  const { data } = await apiClient.post<AiCartridgeDraftProposalView>(`/cartridge-drafts/${id}/reject`, { comment })
  return data
}

/** `GET /ai-runs/{id}` — the linked `AiRun`'s `provenance[]` (citations) and `isStubProduced` flag
 * (honest-stub disclosure, §5). Open canon read, no capability gate (mirrors `AiRunService.get`). */
export async function getAiRun(id: string, signal?: AbortSignal): Promise<AiRunView> {
  const { data } = await apiClient.get<AiRunView>(`/ai-runs/${id}`, { signal })
  return data
}
